import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Minus, Wallet, ArrowUp, ArrowDown, Receipt, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);
const fmtDate = (d) => new Date(d).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });

export default function Wallets() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const initialId = params.get("customer");
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [operation, setOperation] = useState("add"); // add | subtract | set
  const [type, setType] = useState("refund");
  const [newBalance, setNewBalance] = useState("");
  const [confirm, setConfirm] = useState(null); // { operation, amount, newBalanceAfter, description, oldBalance } | null

  const loadCustomers = async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
    if (initialId) {
      const found = data.find((c) => c.customer_id === initialId);
      if (found) setSelected(found);
    }
  };

  useEffect(() => { loadCustomers(); /* eslint-disable-next-line */ }, []);

  const loadTxns = async (id) => {
    if (!id) { setTxns([]); return; }
    const { data } = await api.get(`/wallet/${id}/transactions`);
    setTxns(data);
  };

  useEffect(() => { if (selected) loadTxns(selected.customer_id); }, [selected]);

  const addCredit = (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Selecciona un cliente"); return; }
    const oldBalance = selected.wallet_balance;
    let newBalanceAfter;
    let amt = 0;
    if (operation === "set") {
      const nb = parseFloat(newBalance);
      if (isNaN(nb)) { toast.error("Saldo inválido"); return; }
      newBalanceAfter = Math.round(nb * 100) / 100;
    } else {
      amt = parseFloat(amount);
      if (!amt || amt <= 0) { toast.error("Monto inválido"); return; }
      newBalanceAfter = operation === "subtract"
        ? Math.round((oldBalance - amt) * 100) / 100
        : Math.round((oldBalance + amt) * 100) / 100;
    }
    setConfirm({
      operation,
      amount: amt,
      oldBalance,
      newBalanceAfter,
      description: description.trim(),
      type,
    });
  };

  const executeOperation = async () => {
    if (!confirm || !selected) return;
    try {
      let data;
      if (confirm.operation === "set") {
        const res = await api.post("/wallet/set", {
          customer_id: selected.customer_id,
          new_balance: confirm.newBalanceAfter,
          description: confirm.description || "Saldo establecido manualmente",
        });
        data = res.data;
      } else if (confirm.operation === "subtract") {
        const res = await api.post("/wallet/debit", {
          customer_id: selected.customer_id,
          amount: confirm.amount,
          description: confirm.description || "Ajuste manual (resta)",
        });
        data = res.data;
      } else {
        const res = await api.post("/wallet/credit", {
          customer_id: selected.customer_id,
          amount: confirm.amount,
          description: confirm.description || (confirm.type === "refund" ? "Devolución (saldo a favor)" : "Crédito agregado"),
          type: confirm.type,
        });
        data = res.data;
      }
      toast.success(`Saldo actualizado: $ ${fmt(data.balance)}`);
      setAmount(""); setDescription(""); setNewBalance("");
      setSelected({ ...selected, wallet_balance: data.balance });
      const fresh = customers.map((c) => c.customer_id === selected.customer_id ? { ...c, wallet_balance: data.balance } : c);
      setCustomers(fresh);
      loadTxns(selected.customer_id);
      setConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setConfirm(null);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="wallets-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Crédito en tienda</p>
        <h1 className="text-3xl md:text-4xl font-heading font-bold">Billeteras Virtuales</h1>
        <p className="text-zinc-400 mt-1 text-sm">Saldo a favor para devoluciones y créditos del cliente</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Customer list */}
        <aside className="lg:col-span-4">
          <div className="glass-card rounded-xl">
            <div className="p-4 border-b border-white/5">
              <h2 className="font-heading font-semibold flex items-center gap-2"><Wallet size={18} /> Clientes</h2>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {customers.map((c) => (
                <button
                  key={c.customer_id}
                  onClick={() => setSelected(c)}
                  data-testid={`wallet-customer-${c.customer_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition ${selected?.customer_id === c.customer_id ? "bg-turquoise/10 border-l-2 border-l-turquoise" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">{c.phone || c.email || "—"}</p>
                    </div>
                    <p className={`font-mono text-sm ${c.wallet_balance > 0 ? "text-turquoise" : c.wallet_balance < 0 ? "text-red-400" : "text-zinc-500"}`}>
                      {c.wallet_balance < 0 ? "-" : ""}$ {fmt(Math.abs(c.wallet_balance))}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Detail */}
        <section className="lg:col-span-8 space-y-4">
          {!selected ? (
            <div className="glass-card rounded-xl p-12 text-center text-zinc-500">
              <Wallet size={48} className="mx-auto mb-4 opacity-30" />
              <p>Selecciona un cliente para gestionar su billetera</p>
            </div>
          ) : (
            <>
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono">
                      {selected.wallet_balance < 0 ? "Saldo deudor" : "Saldo actual"}
                    </p>
                    <p className={`text-4xl font-mono font-medium tabular-nums mt-1 ${selected.wallet_balance < 0 ? "text-red-400" : "text-turquoise"}`}>
                      {selected.wallet_balance < 0 ? "-" : ""}$ {fmt(Math.abs(selected.wallet_balance))}
                    </p>
                    <p className="text-sm text-white mt-2">{selected.name}</p>
                  </div>
                </div>

                <form onSubmit={addCredit} className="pt-4 border-t border-white/5 space-y-3">
                  {/* Operation tabs */}
                  <div className="grid grid-cols-3 gap-1.5">
                    <button type="button" onClick={() => setOperation("add")} data-testid="wallet-op-add"
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-mono uppercase tracking-wider transition border ${operation === "add" ? "bg-turquoise/10 border-turquoise text-turquoise" : "bg-white/[0.02] border-white/10 text-zinc-400 hover:bg-white/5"}`}>
                      <Plus size={14} weight="bold" /> Sumar
                    </button>
                    <button type="button" onClick={() => setOperation("subtract")} data-testid="wallet-op-subtract"
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-mono uppercase tracking-wider transition border ${operation === "subtract" ? "bg-red-500/10 border-red-500 text-red-400" : "bg-white/[0.02] border-white/10 text-zinc-400 hover:bg-white/5"}`}>
                      <Minus size={14} weight="bold" /> Restar
                    </button>
                    {isAdmin && (
                      <button type="button" onClick={() => setOperation("set")} data-testid="wallet-op-set"
                        className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] font-mono uppercase tracking-wider transition border ${operation === "set" ? "bg-cyan-500/10 border-cyan-500 text-cyan-400" : "bg-white/[0.02] border-white/10 text-zinc-400 hover:bg-white/5"}`}>
                        <PencilSimple size={14} weight="bold" /> Establecer
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {operation === "add" && (
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Tipo</label>
                        <select value={type} onChange={(e) => setType(e.target.value)}
                          className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm">
                          <option value="refund">Devolución</option>
                          <option value="credit">Crédito manual</option>
                        </select>
                      </div>
                    )}
                    {operation === "set" ? (
                      <div className={isAdmin ? "md:col-span-2" : "md:col-span-2"}>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Nuevo saldo exacto</label>
                        <input type="number" step="0.01" min="0" value={newBalance} onChange={(e) => setNewBalance(e.target.value)}
                          placeholder={fmt(selected.wallet_balance)} data-testid="wallet-newbalance-input"
                          className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                      </div>
                    ) : (
                      <div className={operation === "add" ? "" : "md:col-span-2"}>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">
                          {operation === "subtract" ? "Monto a restar" : "Monto"}
                        </label>
                        <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                          placeholder="0.00" data-testid="wallet-amount-input"
                          className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                      </div>
                    )}
                    <div className="flex items-end">
                      <button type="submit" data-testid="wallet-apply-btn"
                        className={`w-full text-black font-semibold py-2 rounded-md flex items-center justify-center gap-2 ${
                          operation === "subtract"
                            ? "bg-red-400 hover:bg-red-300"
                            : operation === "set"
                              ? "bg-cyan-400 hover:bg-cyan-300"
                              : "bg-brand-gradient hover:opacity-90"
                        }`}>
                        {operation === "subtract" ? <><Minus size={16} weight="bold" /> Restar</> : operation === "set" ? <><PencilSimple size={16} weight="bold" /> Aplicar</> : <><Plus size={16} weight="bold" /> Sumar</>}
                      </button>
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Descripción / motivo</label>
                      <input value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder={operation === "subtract" ? "Ej: corrección de saldo" : operation === "set" ? "Ej: ajuste por inventario" : "Ej: Devolución artículo X"}
                        className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </form>
              </div>

              <div className="glass-card rounded-xl">
                <div className="p-4 border-b border-white/5">
                  <h3 className="font-heading font-semibold flex items-center gap-2"><Receipt size={16} /> Movimientos</h3>
                </div>
                <div className="max-h-[50vh] overflow-y-auto">
                  {txns.length === 0 ? (
                    <p className="p-12 text-center text-zinc-500 text-sm">Sin movimientos</p>
                  ) : txns.map((t) => (
                    <div key={t.txn_id} className="flex items-center justify-between p-4 border-b border-white/5">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.type === "debit" ? "bg-red-500/10 text-red-400" : "bg-turquoise/10 text-turquoise"}`}>
                          {t.type === "debit" ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                        </div>
                        <div>
                          <p className="text-sm">{t.description}</p>
                          <p className="text-[10px] text-zinc-500 font-mono">{fmtDate(t.created_at)} · {t.operator_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono font-medium ${t.type === "debit" ? "text-red-400" : "text-turquoise"}`}>
                          {t.type === "debit" ? "-" : "+"} $ {fmt(t.amount)}
                        </p>
                        <p className="text-[10px] text-zinc-600 font-mono">saldo: $ {fmt(t.balance_after)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="wallet-confirm-modal">
          <div className="glass-card rounded-xl max-w-md w-full p-6 border border-white/10">
            <div className="mb-5">
              <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-2">Confirmar ajuste</p>
              <h3 className="text-xl font-heading font-bold">
                {confirm.operation === "set" ? "Establecer saldo" : confirm.operation === "subtract" ? "Restar del saldo" : "Sumar al saldo"}
              </h3>
              <p className="text-sm text-zinc-400 mt-1">Cliente: <span className="text-white">{selected?.name}</span></p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Saldo actual</span>
                <span className={`font-mono text-sm ${confirm.oldBalance < 0 ? "text-red-400" : "text-zinc-300"}`}>
                  {confirm.oldBalance < 0 ? "-" : ""}$ {fmt(Math.abs(confirm.oldBalance))}
                </span>
              </div>
              {confirm.operation !== "set" && (
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-xs uppercase tracking-wider text-zinc-500 font-mono">
                    {confirm.operation === "subtract" ? "A restar" : "A sumar"}
                  </span>
                  <span className={`font-mono text-sm ${confirm.operation === "subtract" ? "text-red-400" : "text-turquoise"}`}>
                    {confirm.operation === "subtract" ? "-" : "+"} $ {fmt(confirm.amount)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-3 mt-2 bg-white/[0.03] rounded-md px-3">
                <span className="text-xs uppercase tracking-wider text-turquoise font-mono">Nuevo saldo</span>
                <span className={`font-mono text-lg font-medium tabular-nums ${confirm.newBalanceAfter < 0 ? "text-red-400" : "text-turquoise"}`}>
                  {confirm.newBalanceAfter < 0 ? "-" : ""}$ {fmt(Math.abs(confirm.newBalanceAfter))}
                </span>
              </div>
              {confirm.newBalanceAfter < 0 && (
                <p className="text-[11px] text-red-400/80 font-mono">⚠ El cliente quedará con saldo deudor</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirm(null)}
                data-testid="wallet-confirm-cancel-btn"
                className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5 transition"
              >
                Cancelar
              </button>
              <button
                onClick={executeOperation}
                data-testid="wallet-confirm-apply-btn"
                className={`flex-1 py-2.5 rounded-md text-sm font-semibold text-black transition ${
                  confirm.operation === "subtract"
                    ? "bg-red-400 hover:bg-red-300"
                    : confirm.operation === "set"
                      ? "bg-cyan-400 hover:bg-cyan-300"
                      : "bg-brand-gradient hover:opacity-90"
                }`}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
